import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { cn } from "@/utils/cn";

interface PageShellProps {
    children: ReactNode;
    className?: string;
    /** 배경 vignette 세기. */
    vignette?: "none" | "soft" | "strong";
}

/**
 * 일반 씬의 외곽 껍데기. absolute full + ink 배경 + 선택적 vignette.
 * Home / RunForge / CharacterSheet 등이 공통 사용.
 */
export function PageShell({ children, className, vignette = "soft" }: PageShellProps) {
    const vignetteStyle =
        vignette === "none"
            ? {}
            : vignette === "strong"
                ? {
                    background: `radial-gradient(ellipse at 50% 28%, ${tokens.color.burgundy}2C 0%, transparent 54%), radial-gradient(ellipse at 52% 100%, ${tokens.color.ember}20 0%, transparent 48%), radial-gradient(ellipse at 18% 16%, ${tokens.color.brass}10 0%, transparent 34%)`,
                }
                : {
                    background: `radial-gradient(ellipse at 50% 38%, ${tokens.color.burgundy}24 0%, transparent 58%), radial-gradient(ellipse at 78% 92%, ${tokens.color.brass}10 0%, transparent 34%)`,
                };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className={cn("absolute inset-0 ink-bg", className)}
        >
            <div className="absolute inset-0" style={vignetteStyle} />
            <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                animate={{
                    opacity: [0.12, 0.18, 0.12],
                    scale: [1, 1.02, 1],
                }}
                transition={{
                    duration: 12,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    background: `radial-gradient(ellipse at 50% 18%, ${tokens.color.parchment}12 0%, transparent 44%), radial-gradient(ellipse at 50% 100%, ${tokens.color.ember}12 0%, transparent 34%), radial-gradient(ellipse at 16% 12%, ${tokens.color.brass}0E 0%, transparent 30%)`,
                }}
            />
            <AmbientMotes />
            <div className="relative w-full h-full">{children}</div>
        </motion.div>
    );
}

const MOTES = [
    { left: 8, top: 76, size: 3, dx: 16, dy: -28, duration: 11, delay: 0.2, peak: 0.24 },
    { left: 18, top: 54, size: 4, dx: -10, dy: -22, duration: 13, delay: 1.8, peak: 0.28 },
    { left: 27, top: 68, size: 2, dx: 12, dy: -18, duration: 9, delay: 0.7, peak: 0.18 },
    { left: 41, top: 38, size: 3, dx: -8, dy: -20, duration: 12, delay: 2.6, peak: 0.22 },
    { left: 56, top: 72, size: 4, dx: 10, dy: -26, duration: 14, delay: 1.2, peak: 0.3 },
    { left: 67, top: 48, size: 3, dx: -14, dy: -16, duration: 10, delay: 3.4, peak: 0.2 },
    { left: 79, top: 64, size: 2, dx: 9, dy: -24, duration: 12, delay: 2.1, peak: 0.18 },
    { left: 89, top: 44, size: 4, dx: -12, dy: -22, duration: 13, delay: 4.2, peak: 0.26 },
];

function AmbientMotes() {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {MOTES.map((mote, index) => (
                <motion.span
                    key={index}
                    initial={{ opacity: 0, x: 0, y: 0, scale: 0.9 }}
                    animate={{
                        opacity: [0, mote.peak, mote.peak * 0.6, 0],
                        x: [0, mote.dx, -mote.dx * 0.35, mote.dx * 0.15],
                        y: [0, mote.dy * 0.5, mote.dy, mote.dy * 1.15],
                        scale: [0.9, 1.1, 1, 0.95],
                    }}
                    transition={{
                        duration: mote.duration,
                        delay: mote.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                        times: [0, 0.2, 0.75, 1],
                    }}
                    style={{
                        position: "absolute",
                        left: `${mote.left}%`,
                        top: `${mote.top}%`,
                        width: mote.size,
                        height: mote.size,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${tokens.color.parchment} 0%, ${tokens.color.ember}AA 45%, transparent 75%)`,
                        boxShadow: `0 0 ${mote.size * 3}px ${tokens.color.ember}55, 0 0 ${mote.size * 6}px ${tokens.color.brass}22`,
                        filter: "blur(0.4px)",
                    }}
                />
            ))}
        </div>
    );
}
